/**
 * MIA AI API Service
 * Handles all API calls for MIA AI features
 * Matches Flutter's Api.request pattern
 */

import { apiUrl } from "@/utils/api";

/**
 * Get authentication headers
 * @returns {Object} headers object
 */
function getAuthHeaders() {
  const token = localStorage.getItem("access_token");
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (
    typeof window !== "undefined" &&
    window.appRegion &&
    window.appRegion.phone_code
  ) {
    headers["x-country-code"] = window.appRegion.phone_code;
  }
  return headers;
}

/**
 * Safely parse JSON response, handling HTML error pages
 * @param {Response} response - Fetch response object
 * @returns {Promise<{json: any, parseError: string|null}>}
 */
async function safeParseJson(response) {
  const contentType = response.headers.get("content-type") || "";

  // Check if response is JSON
  if (!contentType.includes("application/json")) {
    const text = await response.text();
    // Check if it's an HTML page (likely a redirect or error page)
    if (text.startsWith("<!DOCTYPE") || text.startsWith("<html")) {
      return {
        json: null,
        parseError: `Server returned HTML instead of JSON. Status: ${response.status}. This may indicate an authentication issue or incorrect endpoint.`,
      };
    }
    // Try to parse as JSON anyway (some servers don't set content-type correctly)
    try {
      return { json: JSON.parse(text), parseError: null };
    } catch (e) {
      return {
        json: null,
        parseError: `Invalid response format. Expected JSON but got: ${text.substring(
          0,
          100
        )}...`,
      };
    }
  }

  try {
    const json = await response.json();
    return { json, parseError: null };
  } catch (e) {
    return {
      json: null,
      parseError: `Failed to parse JSON response: ${e.message}`,
    };
  }
}

/**
 * Make a GET request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} queryParams - Optional query parameters
 * @returns {Promise<{data: any, error: any}>}
 */
export async function apiGet(endpoint, queryParams = {}) {
  try {
    let url = `${apiUrl}/${endpoint}`;

    // Add query parameters
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    console.log("[MIA API] GET:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    const { json, parseError } = await safeParseJson(response);

    if (parseError) {
      console.error("[MIA API] Parse error:", parseError);
      return {
        data: null,
        error: {
          message: parseError,
          code: "PARSE_ERROR",
        },
      };
    }

    if (!response.ok) {
      return {
        data: null,
        error: {
          message: json?.error?.message || json?.message || "Request failed",
          code: json?.error?.code || response.status,
        },
      };
    }

    return {
      data: json.data || json,
      error: null,
    };
  } catch (e) {
    console.error("[MIA API] GET error:", e);
    return {
      data: null,
      error: {
        message: e.message || "Network error",
        code: "NETWORK_ERROR",
      },
    };
  }
}

/**
 * Make a POST request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} body - Request body
 * @returns {Promise<{data: any, error: any}>}
 */
export async function apiPost(endpoint, body = {}) {
  try {
    const url = `${apiUrl}/${endpoint}`;
    console.log("[MIA API] POST:", url, body);

    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const { json, parseError } = await safeParseJson(response);

    if (parseError) {
      console.error("[MIA API] Parse error:", parseError);
      return {
        data: null,
        error: {
          message: parseError,
          code: "PARSE_ERROR",
        },
      };
    }

    if (!response.ok) {
      return {
        data: null,
        error: {
          message: json?.error?.message || json?.message || "Request failed",
          code: json?.error?.code || response.status,
        },
      };
    }

    return {
      data: json.data || json,
      error: null,
    };
  } catch (e) {
    console.error("[MIA API] POST error:", e);
    return {
      data: null,
      error: {
        message: e.message || "Network error",
        code: "NETWORK_ERROR",
      },
    };
  }
}

/**
 * Make a PUT request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} body - Request body
 * @returns {Promise<{data: any, error: any}>}
 */
export async function apiPut(endpoint, body = {}) {
  try {
    const url = `${apiUrl}/${endpoint}`;
    console.log("[MIA API] PUT:", url, body);

    const response = await fetch(url, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const { json, parseError } = await safeParseJson(response);

    if (parseError) {
      console.error("[MIA API] Parse error:", parseError);
      return {
        data: null,
        error: {
          message: parseError,
          code: "PARSE_ERROR",
        },
      };
    }

    if (!response.ok) {
      return {
        data: null,
        error: {
          message: json?.error?.message || json?.message || "Request failed",
          code: json?.error?.code || response.status,
        },
      };
    }

    return {
      data: json.data || json,
      error: null,
    };
  } catch (e) {
    console.error("[MIA API] PUT error:", e);
    return {
      data: null,
      error: {
        message: e.message || "Network error",
        code: "NETWORK_ERROR",
      },
    };
  }
}

/**
 * Upload a file with progress tracking
 * Matches Flutter's multipart file upload with onSendProgress
 * @param {string} endpoint - API endpoint
 * @param {File} file - File to upload
 * @param {Object} extraData - Additional form data
 * @param {Function} onProgress - Progress callback (sent, total)
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{data: any, error: any}>}
 */
export async function apiUploadFile(
  endpoint,
  file,
  extraData = {},
  onProgress = null,
  signal = null
) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    // Handle abort signal
    if (signal) {
      signal.addEventListener("abort", () => {
        xhr.abort();
        resolve({
          data: null,
          error: {
            message: "Upload cancelled",
            code: "CANCELLED",
          },
        });
      });
    }

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
        }
      });
    }

    // Handle completion
    xhr.addEventListener("load", () => {
      const contentType = xhr.getResponseHeader("content-type") || "";
      const responseText = xhr.responseText;

      console.log("[MIA API] Upload response status:", xhr.status);

      // Check if response is HTML (likely error page)
      if (
        !contentType.includes("application/json") &&
        (responseText.startsWith("<!DOCTYPE") ||
          responseText.startsWith("<html"))
      ) {
        console.error("[MIA API] Upload received HTML instead of JSON");
        resolve({
          data: null,
          error: {
            message: `Server returned HTML instead of JSON. Status: ${xhr.status}. This may indicate an authentication issue.`,
            code: "PARSE_ERROR",
          },
        });
        return;
      }

      try {
        const json = JSON.parse(responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({
            data: json.data || json,
            error: null,
          });
        } else {
          resolve({
            data: null,
            error: {
              message: json?.error?.message || json?.message || "Upload failed",
              code: json?.error?.code || xhr.status,
            },
          });
        }
      } catch (e) {
        console.error(
          "[MIA API] Upload parse error:",
          e,
          "Response:",
          responseText.substring(0, 200)
        );
        resolve({
          data: null,
          error: {
            message: "Invalid response format",
            code: "PARSE_ERROR",
          },
        });
      }
    });

    // Handle errors
    xhr.addEventListener("error", () => {
      resolve({
        data: null,
        error: {
          message: "Network error",
          code: "NETWORK_ERROR",
        },
      });
    });

    xhr.addEventListener("abort", () => {
      resolve({
        data: null,
        error: {
          message: "Upload cancelled",
          code: "CANCELLED",
        },
      });
    });

    // Prepare form data
    const formData = new FormData();
    formData.append("file", file, file.name);

    // Add extra data fields
    Object.entries(extraData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });

    // Open and send request
    const url = `${apiUrl}/${endpoint}`;
    console.log("[MIA API] Upload:", url);
    xhr.open("POST", url);

    // Set auth headers (but not Content-Type - let browser set it for FormData)
    xhr.setRequestHeader("Accept", "application/json");
    const token = localStorage.getItem("access_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    if (
      typeof window !== "undefined" &&
      window.appRegion &&
      window.appRegion.phone_code
    ) {
      xhr.setRequestHeader("x-country-code", window.appRegion.phone_code);
    }

    xhr.send(formData);
  });
}

/**
 * Paginated GET request
 * @param {string} endpoint - API endpoint
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 * @param {Object} queryParams - Additional query parameters
 * @returns {Promise<{data: any[], meta: any, error: any}>}
 */
export async function apiGetPaginated(
  endpoint,
  page = 1,
  perPage = 15,
  queryParams = {}
) {
  const result = await apiGet(endpoint, {
    page,
    per_page: perPage,
    ...queryParams,
  });

  if (result.error) {
    return {
      data: [],
      meta: null,
      error: result.error,
    };
  }

  // Handle different response formats
  const responseData = result.data;

  if (Array.isArray(responseData)) {
    return {
      data: responseData,
      meta: null,
      error: null,
    };
  }

  if (responseData?.data && Array.isArray(responseData.data)) {
    return {
      data: responseData.data,
      meta: responseData.meta || {
        current_page: page,
        per_page: perPage,
        total: responseData.total || responseData.data.length,
      },
      error: null,
    };
  }

  return {
    data: [],
    meta: null,
    error: null,
  };
}
